import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
const PRIVACY_STORAGE_KEY = 'lighthouse_candidate_privacy';

export type CandidatePrivacySettings = {
  profileVisible: boolean;
};

const defaultPrivacy: CandidatePrivacySettings = { profileVisible: true };

export async function fetchCandidatePrivacySettings(): Promise<CandidatePrivacySettings> {
  if (!API_BASE_URL) {
    if (typeof window === 'undefined') return defaultPrivacy;
    const raw = window.localStorage.getItem(PRIVACY_STORAGE_KEY);
    if (!raw) return defaultPrivacy;
    try {
      const parsed = JSON.parse(raw) as CandidatePrivacySettings;
      return {
        profileVisible:
          typeof parsed.profileVisible === 'boolean' ? parsed.profileVisible : defaultPrivacy.profileVisible,
      };
    } catch {
      return defaultPrivacy;
    }
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate/settings/privacy`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Privacy settings fetch failed (${response.status})`);
  }
  const payload = (await response.json()) as CandidatePrivacySettings;
  return payload ?? defaultPrivacy;
}

export async function updateCandidatePrivacySettings(
  settings: CandidatePrivacySettings,
): Promise<CandidatePrivacySettings> {
  if (!API_BASE_URL) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify(settings));
    }
    return settings;
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate/settings/privacy`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Privacy settings update failed (${response.status}): ${text}`);
  }

  return (await response.json()) as CandidatePrivacySettings;
}
